import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw, AlertCircle } from "lucide-react";
import { billingAPI } from "../services/api/billing";
import { useToast } from "../contexts/ToastContext";
import { usePageTitle } from "../contexts/PageTitleContext";
import { useAuth } from "../contexts/AuthContext";
import Button from "../components/ui/Button";
import CurrentPlanCard from "../components/billing/CurrentPlanCard";
import CreditsCard from "../components/billing/CreditsCard";
import OutOfCreditsCard from "../components/billing/OutOfCreditsCard";
import PricingInfoCard from "../components/billing/PricingInfoCard";
import UsageAlertsCard from "../components/billing/UsageAlertsCard";
import ManageBillingCard from "../components/billing/ManageBillingCard";
import TransactionsLedger from "../components/billing/TransactionsLedger";
import UpgradeModal from "../components/billing/UpgradeModal";
import DowngradeModal from "../components/billing/DowngradeModal";
import BuyCreditsModal from "../components/billing/BuyCreditsModal";
import ConfirmPlanChangeModal from "../components/billing/ConfirmPlanChangeModal";

export default function Billing() {
  const { showToast } = useToast();
  const { setPageTitle, setParentPage } = usePageTitle();
  const { currentWorkspace } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [subscription, setSubscription] = useState(null);

  // Derived from this page's OWN load(), deliberately not from useEntitlements().
  // That hook fetches /billing/summary independently, so a transient failure in one
  // and not the other left them disagreeing — its SAFE_DEFAULT says isPayg:false, and
  // a pay-as-you-go account then rendered the Growth two-column layout, losing the
  // prominent credit card, the out-of-credits panel, and the isPayg prop
  // ManageBillingCard needs to hide actions a $0 account should not be offered.
  //
  // plan.code is the NESTED shape /subscription/current returns, which is what
  // CurrentPlanCard already keys off — so the page and the card cannot disagree.
  // One source of truth per value, and one fewer request on mount.
  const isPayg = subscription?.plan?.code === "pay_as_you_go";
  const totalCredits = Number(summary?.total_balance ?? 0);
  const outOfCredits = summary != null && totalCredits <= 0;
  const welcomeCredits = summary?.welcome_credits ?? null;
  const [packs, setPacks] = useState([]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [downgradeOpen, setDowngradeOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [reactivateConfirmOpen, setReactivateConfirmOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [submitting, setSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const returnHandledRef = useRef("");

  useEffect(() => {
    setParentPage({ name: "Settings", href: "/settings/workspace" });
    setPageTitle("Billing");
    return () => {
      setParentPage(null);
      setPageTitle(null);
    };
  }, [setParentPage, setPageTitle]);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [s, sub, p] = await Promise.all([
        billingAPI.getBillingSummary(currentWorkspace?.id),
        billingAPI.getCurrentSubscription(currentWorkspace?.id),
        billingAPI.listTopUpPacks()
      ]);
      setSummary(s); setSubscription(sub); setPacks(p);
    } catch (e) {
      const msg = e.response?.data?.error || "Failed to load billing";
      setLoadError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch whenever the user switches workspaces — billing belongs to the
  // workspace, not the user. Internal admins especially need this since they
  // can browse accounts they don't own.
  useEffect(() => { load(); }, [currentWorkspace?.id]); // eslint-disable-line

  // All mutations guard on `submitting` so a rapid double-click can't fire two
  // requests (e.g. two Stripe Checkout sessions), and surface the backend's
  // actionable error (multi-account 422, account_read_only) instead of a
  // generic message.
  const onManageCards = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { portal_url } = await billingAPI.openBillingPortal();
      window.location.href = portal_url;
    } catch (e) {
      showToast(e.response?.data?.error || "Could not open billing portal", "error");
      setSubmitting(false);
    }
  };

  const onCancel = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await billingAPI.cancelSubscription();
      await load();
      showToast("Subscription will cancel at period end", "success");
    } catch (e) {
      showToast(e.response?.data?.error || "Failed to cancel", "error");
    } finally { setSubmitting(false); }
  };

  // One endpoint, three meanings: it releases the Stripe subscription schedule and
  // clears cancel_at_period_end + the pending plan. Which of those the user actually
  // asked for decides the wording, so read the state before it's reloaded.
  //
  // A one-click "Keep {plan}" / reactivate previously undid a real scheduled
  // downgrade by accident (July incident) — so the button now only opens a
  // confirmation; this function is the actual API call, fired solely from the
  // confirm modal below.
  const doReactivate = async () => {
    if (submitting) return;
    const pendingPlanName = subscription?.pending_plan?.name;
    const wasCanceling = Boolean(subscription?.cancel_at_period_end);
    setSubmitting(true);
    try {
      await billingAPI.reactivateSubscription();
      await load();
      showToast(
        pendingPlanName
          ? `Downgrade to ${pendingPlanName} canceled — staying on ${subscription?.plan?.name}`
          : wasCanceling
            ? "Cancellation stopped — your subscription will renew"
            : "Subscription reactivated",
        "success"
      );
      setReactivateConfirmOpen(false);
    } catch (e) {
      showToast(e.response?.data?.error || "Failed to reactivate", "error");
    } finally { setSubmitting(false); }
  };

  const onReactivate = () => {
    if (submitting) return;
    setReactivateConfirmOpen(true);
  };

  // FE-C1 + I3: handle returns from Stripe Checkout and the banner deep-links.
  // Stripe's webhook syncs ASYNCHRONOUSLY, so on ?subscribed=1 / ?topup=1 we
  // poll until the subscription flips to active (or give up after ~16s) instead
  // of leaving the user on the stale state. ?buy=1 is the banner / deep-link
  // affordance that pops the Buy-credits modal straight open.
  useEffect(() => {
    if (loading) return; // wait for the initial load so `subscription` is known
    const subscribed = searchParams.get("subscribed");
    const topup = searchParams.get("topup");
    const buy = searchParams.get("buy");
    const upgrade = searchParams.get("upgrade");
    const key = `${subscribed || ""}|${topup || ""}|${buy || ""}|${upgrade || ""}`;
    if (key === "|||") return;                        // no relevant params
    if (returnHandledRef.current === key) return;    // process each return once
    returnHandledRef.current = key;

    const clearParams = () => {
      ["subscribed", "topup", "buy", "upgrade"].forEach((k) => searchParams.delete(k));
      setSearchParams(searchParams, { replace: true });
    };

    if (buy === "1") { clearParams(); setBuyOpen(true); return; }
    // Deep-link from UpgradeGate (a blocked Growth-only route/API call) —
    // pop the plan-upgrade modal straight open.
    if (upgrade === "1") { clearParams(); setUpgradeOpen(true); return; }

    if (subscribed === "1" || topup === "1") {
      let cancelled = false;
      (async () => {
        showToast("Payment received — finalizing your account…", "info");
        for (let i = 0; i < 8 && !cancelled; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          try {
            const sub = await billingAPI.getCurrentSubscription(currentWorkspace?.id);
            // subscription synced once it's active & not pending-cancel.
            if (subscribed === "1" && sub?.status === "active" && !sub?.cancel_at_period_end) break;
            if (topup === "1") break; // top-up balance reflects on the reload below
          } catch { /* keep polling */ }
        }
        if (cancelled) return;
        await load();
        showToast(subscribed === "1" ? "Subscription active." : "Credits added.", "success");
        clearParams();
      })();
      return () => { cancelled = true; };
    }
    if (subscribed === "0" || topup === "0") { showToast("Checkout canceled.", "info"); clearParams(); return; }
    // eslint-disable-next-line
  }, [searchParams, loading]);

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[200px]">
        <RefreshCw className="h-5 w-5 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (loadError || !summary) {
    return (
      <div className="p-4">
        <div className="bg-card rounded-2xl border border-border shadow-sm p-4 flex flex-col items-center text-center gap-4">
          <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-red-500" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground mb-1">Couldn't load billing</h2>
            <p className="text-sm text-muted-foreground">{loadError || "Please try again."}</p>
          </div>
          <Button variant="secondary" onClick={load}>
            <RefreshCw className="h-4 w-4" />
            <span>Retry</span>
          </Button>
        </div>
      </div>
    );
  }

  // Copy for the reactivate/"Keep {plan}" confirmation — covers both a
  // scheduled downgrade to another plan and a bare cancel-at-period-end.
  const pendingPlanName = subscription?.pending_plan?.name;
  const currentPlanName = subscription?.plan?.name;
  const monthlyPrice = subscription?.plan?.monthly_price_cents;
  const periodEndDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString()
    : null;
  const reactivateBody =
    `This cancels the scheduled change${pendingPlanName ? ` to ${pendingPlanName}` : ""}. ` +
    `You'll stay on ${currentPlanName}${
      monthlyPrice ? ` and be billed $${(monthlyPrice / 100).toFixed(0)}${periodEndDate ? ` on ${periodEndDate}` : ""}` : ""
    }.`;

  const tabs = [
    { id: "overview",     label: "Overview" },
    { id: "transactions", label: "Transactions" }
  ];

  return (
    <div className="p-4">
      {/* Pill-style tabs matching Scenario page default */}
      <div className="flex space-x-1 mb-6 bg-muted rounded-lg p-1 w-fit">
        {tabs.map(t => (
          <Button
            key={t.id}
            variant="tab"
            onClick={() => setActiveTab(t.id)}
            className={
              activeTab === t.id
                ? "text-sm bg-card text-foreground shadow-sm"
                : "text-sm text-muted-foreground hover:text-foreground"
            }
          >
            {t.label}
          </Button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="space-y-6">
          {!loading && isPayg ? (
            // Pay-as-you-go: no plan allocation, so credits ARE the story.
            // Lead with the prominent CreditsCard; the plan card drops below
            // it purely as an upgrade affordance. When the balance hits zero
            // a dedicated out-of-credits panel sits above everything.
            <>
              {outOfCredits && (
                <OutOfCreditsCard
                  onBuyCredits={() => setBuyOpen(true)}
                  onUpgrade={() => setUpgradeOpen(true)}
                />
              )}

              <CreditsCard
                summary={summary}
                prominent
                onBuyCredits={() => setBuyOpen(true)}
                welcomeCredits={welcomeCredits}
              />

              <CurrentPlanCard
                subscription={subscription}
                onUpgrade={() => setUpgradeOpen(true)}
                onReactivate={onReactivate}
              />

              <PricingInfoCard summary={summary} />
              <UsageAlertsCard />

              <ManageBillingCard
                subscription={subscription}
                summary={summary}
                isPayg
                onDowngrade={() => setDowngradeOpen(true)}
                onCancel={onCancel}
                onReactivate={onReactivate}
                onManageCards={onManageCards}
              />
            </>
          ) : (
            <>
              {/* Plan + Credits — side by side, equal heights */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                <CurrentPlanCard
                  subscription={subscription}
                  onUpgrade={() => setUpgradeOpen(true)}
                  onReactivate={onReactivate}
                />
                <CreditsCard
                  summary={summary}
                  onBuyCredits={() => setBuyOpen(true)}
                  welcomeCredits={welcomeCredits}
                />
              </div>

              <PricingInfoCard summary={summary} />
              <UsageAlertsCard />

              {/* Secondary actions live here, below usage alerts */}
              <ManageBillingCard
                subscription={subscription}
                summary={summary}
                onDowngrade={() => setDowngradeOpen(true)}
                onCancel={onCancel}
                onReactivate={onReactivate}
                onManageCards={onManageCards}
              />
            </>
          )}
        </div>
      )}

      {activeTab === "transactions" && (
        <div className="space-y-6">
          <TransactionsLedger defaultExpanded clientId={currentWorkspace?.id} />
        </div>
      )}

      {/* Support footer */}
      <div className="text-center text-xs text-muted-foreground pt-8 pb-2">
        Something looking off? Stripe is the source of truth for all charges and plan changes.{" "}
        <a href="mailto:noor@rubrichq.io?subject=Billing%20issue" className="text-foreground hover:text-foreground underline underline-offset-2">
          Contact support
        </a>
      </div>

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        currentCode={subscription?.plan?.code}
        currentStatus={subscription?.status}
        currentPeriodEnd={subscription?.current_period_end}
        currentPlanName={currentPlanName}
        onSuccess={load}
        hasActiveSubscription={
          // "Can we mutate an existing PAID Stripe subscription in place?" — which is
          // what routes UpgradeModal to upgradePlan() instead of Checkout.
          //
          // Pay-as-you-go must answer NO even though it now has a real $0 Stripe
          // subscription, because the account has no payment method on file: swapping
          // the $0 price for Growth's $399 would invoice a customer with no card and
          // strand the subscription in `incomplete`. PAYG upgrades go through Checkout,
          // which collects the card and creates the Growth subscription.
          //
          // Do NOT reduce this to the stripe_subscription_id check alone. That was the
          // original bug: it read `status === "active"` (true for every PAYG account),
          // routed to upgradePlan(), and the backend 500s on "No Stripe subscription" —
          // blocking all self-serve conversion. Gating on the id fixed it only while
          // PAYG had no Stripe subscription; now that it does, the plan code is what
          // carries the meaning.
          subscription?.plan?.code !== "pay_as_you_go" &&
          !!subscription?.stripe_subscription_id &&
          (subscription.status === "active" || subscription.status === "past_due")
        }
      />
      <DowngradeModal
        open={downgradeOpen}
        onClose={() => setDowngradeOpen(false)}
        currentCode={subscription?.plan?.code}
        currentStatus={subscription?.status}
        currentPeriodEnd={subscription?.current_period_end}
        currentPlanName={currentPlanName}
        onSuccess={load}
      />
      <BuyCreditsModal open={buyOpen} onClose={() => setBuyOpen(false)} packs={packs} />
      <ConfirmPlanChangeModal
        open={reactivateConfirmOpen}
        title={`Keep ${currentPlanName}?`}
        confirmLabel={`Keep ${currentPlanName}`}
        cancelLabel="Cancel"
        onCancel={() => setReactivateConfirmOpen(false)}
        onConfirm={doReactivate}
        loading={submitting}
      >
        <p>{reactivateBody}</p>
      </ConfirmPlanChangeModal>
    </div>
  );
}
